import JwtService from "@/core/services/JwtService";
import IdentityApiService from "@/core/services/apiServices/IdentityApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { normalize } from "@/core/helpers/vueRefHelpers";
import store from "..";
import NotificationDialog from "@/core/helpers/notificationDialog";
import { getWord } from "@/core/helpers/translator";
import BaseApiService from "@/core/services/apiServices/BaseApiService";
import PermissionService from "@/core/services/PermissionService";
import router from "@/router";
import { connections, initConnection } from "@/core/helpers/signalrHelper";
import { HubConnection } from "@microsoft/signalr";
import { clearForm } from "@/core/helpers/formHelpers";
import { clearNotifications } from "@/core/services/NotificationService";

export interface User {
  id: string
  firstname: string;
  lastname: string;
  email: string;
  fullname: string;
  defaultLanguageCode: string;
  isGlobalAdmin: boolean;
  isLDAPUser: boolean;
  companyId: string
}

export interface UserAuthInfo {
  errors: Array<string>;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [];
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    let user = normalize(this.user)
    if (!user || Object.keys(user).length === 0) {
      user = JwtService.getUser();
      if (user && Object.keys(user).length > 0) {
        store.commit(Mutations.SET_USER, user)
      }
    }
    return user
  }

  get currentUserId(): string | null {
    const user = store.getters.currentUser
    if (user) {
      return user.id
    }
    return null;
  }

  get currentCompanyId(): string | null {
    const user = store.getters.currentUser
    if (user) {
      return user.companyId
    }
    return null;
  }

  get isGlobalAdmin(): boolean {
    const user = normalize(this.currentUser)
    return user && user.isGlobalAdmin
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    this.user = {
      id: user.id,
      companyId: user.companyId,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      fullname: `${user.firstname} ${user.lastname}`,
      defaultLanguageCode: user.defaultLanguageCode,
      isGlobalAdmin: user.isGlobalAdmin,
      isLDAPUser: user.isLDAPUser
    }

    PermissionService.saveClaims(user.claims ? user.claims.map(function (claim) {
      return `${claim.module}.${claim.name}`
    }) : [])
    JwtService.saveUser(this.user)
    this.errors = [];
  }

  @Mutation
  [Mutations.SET_TOKEN](token) {
    JwtService.saveToken(token);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
    JwtService.saveUser(this.user)
  }

  @Mutation
  [Mutations.PURGE_AUTH](logout) {
    const userId = store.getters.currentUserId;
    if (userId) {
      IdentityApiService.Membership.Auth.Logout(userId);
    }

    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    JwtService.saveUser(this.user);
    JwtService.destroyToken();

    const conns = connections
    if (conns) {
      conns.forEach(function (conn: HubConnection) {
        conn.stop()
      })
    }

    if (logout) {
      router.push({ name: "sign-in", query: { redirectTo: router.currentRoute.value.fullPath } })
    }
  }

  @Action
  [Actions.LOGIN](credentials) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.Auth.Login(credentials)
        .then(({ data }) => {
          if (data.data) {
            this.context.commit(Mutations.SET_AUTH, data.data);
            this.context.commit(Mutations.SET_LANGUAGE, data.data.defaultLanguageCode)
            JwtService.saveLanguage(data.data.defaultLanguageCode)
            JwtService.saveCredentials(credentials)
            this.context.commit(Mutations.SET_TOKEN, data.token);
          }
          else {
            if (data.messages && data.messages[0]) {
              NotificationDialog.Error(getWord(data.messages[0].description));
              this.context.commit(Mutations.SET_ERROR,);
            }
          }
          resolve();
        })
        .catch((error) => {
          BaseApiService.HandleError(error)
          reject();
        })
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
    JwtService.destroyCredentials();
  }

  @Action
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.User.Create(credentials)
        .then(({ data }) => {
          // this.context.commit(Mutations.SET_AUTH, data.data);
          NotificationDialog.SuccessPrompt("userList", "editUser", data.data.id)
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.REGISTER_WITH_ROBOT](credentials) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.User.CreateWithRobot(credentials)
        .then(({ data }) => {
          NotificationDialog.SuccessPrompt("userList", "editUser", data.data.id)
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.REGISTER_WITH_STUDIO_ROBOT](credentials) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.User.CreateWithStudioRobot(credentials)
        .then(({ data }) => {
          NotificationDialog.SuccessPrompt("userList", "editUser", data.data.id)
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.Auth.ChangePassword(payload)
        .then(() => {
          const email = this.currentUser.email;
          const password = payload.password;
          store.dispatch(Actions.LOGIN, { email: email, password: password })
            .then(() => {
              NotificationDialog.Notify("PasswordChanged");
            });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.VERIFY_AUTH]() {
    const token = JwtService.getToken()
    if (token) {
      IdentityApiService.Membership.Auth.Verify(token).then(() => {
        if (JwtService.getUserHubGroupKey()) {
          initConnection();
        }
      })
    }
    else {
      const credentials = JwtService.getCredentials();
      if (credentials && Object.keys(credentials).length > 0) {
        this.context.dispatch(Actions.LOGIN, credentials);
      }
      else {
        router.push({ name: "sign-in" })
      }
    }
  }

  @Action
  [Actions.UPDATE_USER](payload) {
    return new Promise<void>((resolve, reject) => {
      IdentityApiService.Membership.User.Update(payload)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }
}
